import { useState } from 'react'
import { ImagePlus, Loader2, X } from 'lucide-react'
import { uploadFile } from '@/lib/firestore-crud'

/**
 * Batas sisi terpanjang untuk gambar kecil (avatar, thumbnail).
 *
 * Banner dan gambar konten memakai nilai jauh lebih besar lewat [maxSize] —
 * 512px membuat banner terlihat pecah karena di layar ponsel ia dibentang
 * sampai sekitar 1000-1700px fisik.
 */
const DEFAULT_MAX_SIZE = 512

/**
 * Kualitas encoder WebP. 0.9 praktis tidak terlihat bedanya dari aslinya,
 * tapi ukurannya jauh lebih kecil daripada PNG.
 */
const OUTPUT_QUALITY = 0.9

/** Semua hasil unggah dinormalkan ke WebP — alfa tetap terjaga. */
const OUTPUT_MIME = 'image/webp'

interface ImageUploadProps {
  value?: string
  onChange: (url: string) => void
  folder: string
  compress?: boolean
  /** Batas sisi terpanjang dalam piksel. Gambar lebih kecil tidak diperbesar. */
  maxSize?: number
  /** Teks bantuan di bawah tombol, mis. ukuran yang disarankan. */
  hint?: string
}

export function ImageUpload({
  value,
  onChange,
  folder,
  compress = true,
  maxSize = DEFAULT_MAX_SIZE,
  hint,
}: ImageUploadProps) {
  const [uploading, setUploading] = useState(false)

  const resizeImage = (file: File, maxWidth: number, maxHeight: number): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = (event) => {
        const img = new Image()
        img.src = event.target?.result as string
        img.onload = () => {
          const canvas = document.createElement('canvas')
          let width = img.width
          let height = img.height

          if (width > height) {
            if (width > maxWidth) {
              height *= maxWidth / width
              width = maxWidth
            }
          } else {
            if (height > maxHeight) {
              width *= maxHeight / height
              height = maxHeight
            }
          }

          canvas.width = width
          canvas.height = height
          const ctx = canvas.getContext('2d')
          ctx?.drawImage(img, 0, 0, width, height)
          
          canvas.toBlob(
            (blob) => {
              if (blob) resolve(blob)
              else reject(new Error('Canvas to Blob failed'))
            },
            OUTPUT_MIME,
            OUTPUT_QUALITY,
          )
        }
        img.onerror = (err) => reject(err)
      }
      reader.onerror = (err) => reject(err)
    })
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setUploading(true)
    try {
      let fileToUpload: File = file
      let extension = file.name.split('.').pop() || 'png'

      if (compress) {
        const blob = await resizeImage(file, maxSize, maxSize)
        // Nama berakhiran .webp harus benar-benar berisi WebP: sebelumnya
        // blob memakai MIME aslinya, jadi PNG tersimpan dengan nama .webp.
        fileToUpload = new File([blob], file.name, { type: OUTPUT_MIME })
        extension = 'webp'
      }

      const baseName = file.name.replace(/\.[^.]+$/, '')
      const fileName = `${Date.now()}-${baseName}.${extension}`
      const url = await uploadFile(`${folder}/${fileName}`, fileToUpload)
      onChange(url)
    } catch (error) {
      console.error('Upload failed:', error)
      const code = (error as { code?: string })?.code ?? 'unknown'
      alert(`Upload gagal (${code}). Cek koneksi dan izin Firebase Storage.`)
    } finally {
      setUploading(false)
    }
  }

  const clear = () => onChange('')

  return (
    <div className="space-y-3">
      {value ? (
        <div className="group relative h-40 w-full overflow-hidden rounded-2xl border border-stone-800 bg-stone-900">
          <img
            src={value}
            alt="Preview"
            className="h-full w-full object-cover transition group-hover:scale-105"
          />
          <button
            onClick={clear}
            className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm transition hover:bg-black/70"
            type="button"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <label className="flex h-40 w-full cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-stone-800 bg-[#0A0A0A] transition hover:border-orange-500/50 hover:bg-orange-500/5">
          {uploading ? (
            <>
              <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
              <span className="text-sm font-medium text-stone-500">
                Uploading...
              </span>
            </>
          ) : (
            <>
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-stone-900 shadow-sm border border-stone-800">
                <ImagePlus className="h-5 w-5 text-stone-400" />
              </div>
              <div className="text-center px-4">
                <span className="text-sm font-semibold text-stone-300">
                  Klik untuk upload gambar
                </span>
                <p className="mt-1 text-xs text-stone-500">
                  {hint ??
                    (compress
                      ? `Dikompres ke WebP, sisi terpanjang maks ${maxSize}px`
                      : 'Diunggah apa adanya, tanpa kompresi')}
                </p>
              </div>
            </>
          )}
          <input
            type="file"
            className="hidden"
            accept="image/*"
            onChange={handleFileChange}
            disabled={uploading}
          />
        </label>
      )}
    </div>
  )
}

